import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/baseUi/input-group"
import { cn } from "@/modules/lib/utils"
import SearchEmpty from "@images/empty/search_empty.svg"
import SubscribersEmpty from "@images/empty/subscribers_empty.svg"
import { word } from "@modules/core/utils/i18n"
import useDebounce from "@modules/hooks/useDebounce"
import { ProgramBundleType } from "@modules/programBundle/api/programBundleDto"
import { ProgramBundleEntity } from "@modules/programBundle/entities/programBundleEntity"
import { useProgramBundleRights } from "@modules/programBundle/hooks/useProgramBundleRights"
import { useProgramBundleSubscribers } from "@modules/programBundle/programBundleSubscribers/hooks/useProgramBundleSubscriber"
import { ProgramSubscriberItem } from "@modules/programs/programSubscriber/components/programSubscriberItem"
import { ProgramType } from "@modules/programs/types/programTypes"
import { getSubscriptionCountText } from "@modules/programs/utils/programUtils"
import { EmptyView } from "@modules/ui/components/emptyView"
import { LoadingSpinner } from "@modules/ui/components/loading"
import { Search, X } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import { Text } from "@/components/ui/baseUi/text"

type ProgramBundleInfoSubscribersListProps = {
  programBundle: ProgramBundleEntity
  className?: string
} & React.ComponentProps<"div">

export function ProgramBundleInfoSubscribersList({
  programBundle,
  className,
  ...props
}: ProgramBundleInfoSubscribersListProps) {
  const { isAuthor } = useProgramBundleRights(programBundle.id)
  const loadMoreRef = useRef<HTMLDivElement | null>(null)
  const [searchTerms, setSearchTerms] = useState<string>("")
  const debouncedSearchTerms = useDebounce(searchTerms, 300)
  const {
    data: { content: subscribers },
    isLoading: isLoadingSubscribers,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useProgramBundleSubscribers(programBundle.id, debouncedSearchTerms)

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const isIntersecting = entries[0]?.isIntersecting
      if (isIntersecting && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage()
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  )

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      rootMargin: "100px",
      threshold: 0.1,
    })

    const currentRef = loadMoreRef.current
    if (currentRef) {
      observer.observe(currentRef)

      // Check if the element is already visible on mount (for large screens)
      const rect = currentRef.getBoundingClientRect()
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0
      if (isVisible && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage()
      }
    }

    return () => observer.disconnect()
  }, [handleIntersection, hasNextPage, isFetchingNextPage, fetchNextPage])

  return (
    <div className={cn("mt-4 flex grow flex-col px-4 pt-12", className)} {...props}>
      <Text fontStyle="BS" className="mb-3 text-textDefault-heading">
        {getSubscriptionCountText(
          programBundle.subscriptionCount,
          isAuthor,
          false,
          true,
          programBundle.type === ProgramBundleType.ON_OFF ? ProgramType.ONOFF : ProgramType.VIRTUAL,
        ).toUpperCase()}
      </Text>
      <InputGroup className="mb-4">
        <InputGroupInput
          placeholder={word("program.subscribersList.search.placeholder")}
          onChange={(e) => setSearchTerms(e.currentTarget.value)}
          value={searchTerms}
        />
        <InputGroupAddon align="inline-start">
          <Search />
        </InputGroupAddon>
        {searchTerms && (
          <InputGroupAddon align="inline-end">
            <X className="size-4 cursor-pointer" onClick={() => setSearchTerms("")} />
          </InputGroupAddon>
        )}
      </InputGroup>

      {isLoadingSubscribers ? (
        <LoadingSpinner />
      ) : subscribers.length > 0 ? (
        <div className="flex flex-col">
          {subscribers.map((subscriber) => (
            <ProgramSubscriberItem
              program={programBundle}
              subscriber={subscriber}
              key={subscriber.id}
              canSeeSubscriberDetails={programBundle.type === ProgramBundleType.VIRTUAL}
            />
          ))}
          {(hasNextPage || isFetchingNextPage) && (
            <div ref={loadMoreRef} className="flex h-auto w-full items-center justify-center py-4">
              <Text fontStyle="BM">{word("global.loading")}</Text>
            </div>
          )}
        </div>
      ) : subscribers.length === 0 && !isFetching && searchTerms.length > 0 ? (
        <EmptyView
          image={{ src: SearchEmpty }}
          title={word("explore.search.no_result.title", { searchTerms })}
          description={word("explore.search.no_result.message")}
          flexGrow={false}
          textAlign="left"
          imagePosition="after"
          className="flex w-full bg-surface-card p-0"
        />
      ) : subscribers.length === 0 && debouncedSearchTerms?.length === 0 ? (
        <EmptyView
          image={{ src: SubscribersEmpty }}
          title={
            programBundle.type === ProgramBundleType.ON_OFF
              ? word("program.onOff.subscribers.title.empty")
              : word("program.subscribers.title.empty")
          }
          description={word("program.subscribers.description.empty")}
          flexGrow={false}
          className="bg-surface-card p-6"
        />
      ) : null}
    </div>
  )
}
